import React, { useEffect, useState } from "react";
import axios from "axios";
import moment from "moment";
import Select from "react-select";
import { saveAs } from 'file-saver';
import ReactPaginate from 'react-paginate';
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs from "dayjs";
import {
    Card, Row, Col, Form, Table, Button, Badge,
    Spinner, Alert, Modal, InputGroup, Tabs, Tab, Offcanvas
} from 'react-bootstrap';

const ReceivableAgingReport = () => {
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [clients, setClients] = useState([]);
    const [projects, setProjects] = useState([]);
    const [filters, setFilters] = useState({
        client_id: '',
        project_id: '',
        end_date: moment().format('YYYY-MM-DD')
    });
    const [page, setPage] = useState(1);
    const [perPage] = useState(15); // backend default
    const [pagination, setPagination] = useState({
        current_page: 1,
        last_page: 1,
        total: 0,
        per_page: 15,
    });
    const [appliedFilters, setAppliedFilters] = useState({
        client_id: '',
        project_id: '',
        end_date: moment().format('YYYY-MM-DD')
    });
    const [isSearched, setIsSearched] = useState(false);
    const backendUrl = process.env.REACT_APP_BACKEND_URL;
    const userInfo = JSON.parse(localStorage.getItem("user-info"));
    const origin = localStorage.getItem("user-origin");
    const token = userInfo?.token;
    const shouldHideCreditNoteItem = (item) => {
        if (!item) return false;
        if (item.credit_note_id) return true;
        if (item.credit_note) return true;
        if (Array.isArray(item.credit_notes) && item.credit_notes.length > 0) return true;
        if (item.status === 'fully_credited') return true;
        return false;
    };

    useEffect(() => {
        fetchDropdownData();
        // fetchAgingData();
    }, []);

    const fetchDropdownData = async () => {
        try {
            // Fetch clients
            const clientsResponse = await axios.get(`${backendUrl}/api/contacts?type=client`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    "Tenant-Origin": origin,
                },
            });
            setClients(clientsResponse.data.Data.map(client => ({
                value: client.id,
                label: client.name
            })));
        } catch (error) {
            console.error("Error fetching dropdown data", error);
        }
    };

    const fetchClientProjects = async (clientId) => {
        try {
            const response = await axios.get(`${backendUrl}/api/projects?client_id=${clientId}`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    "Tenant-Origin": origin,
                },
            });

            setProjects(response.data.Data.map(project => ({
                value: project.id,
                label: project.name
            })));
        } catch (error) {
            console.error("Error fetching client projects", error);
        }
    };

    const fetchAllProjects = async () => {
        try {
            const response = await axios.get(`${backendUrl}/api/projects`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    "Tenant-Origin": origin,
                },
            });

            setProjects(response.data.Data.map(project => ({
                value: project.id,
                label: project.name
            })));
        } catch (error) {
            console.error("Error fetching all projects", error);
        }
    };

    useEffect(() => {
        if (filters.client_id) {
            fetchClientProjects(filters.client_id);
        } else {
            fetchAllProjects();
        }
    }, [filters.client_id]);

    const fetchAgingData = async () => {
        try {
            setLoading(true);
            const params = {
                client_id: filters.client_id,
                project_id: filters.project_id,
                end_date: filters.end_date,
                page,
                per_page: perPage,
            };

            const response = await axios.get(`${backendUrl}/api/receivable`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    "Tenant-Origin": origin,
                },
                params
            });

            const rawData = response.data.data || [];
            const filteredData = rawData.filter(item => !shouldHideCreditNoteItem(item));
            setData(filteredData);
            setPagination(response.data.pagination || pagination);

        } catch (error) {
            console.error("Error fetching aging report", error);
        } finally {
            setLoading(false);
        }
    };
    const handlePageClick = (event) => {
        setPage(event.selected + 1); // react-paginate is 0-based
    };

    const calculateAgingSummary = (items) => {
        const summary = {
            current: 0,
            '1-30': 0,
            '31-60': 0,
            '61-90': 0,
            '90+': 0,
            total: 0,
            totalPaid: 0,
            totalInvoiced: 0
        };

        items.forEach(item => {
            const amount = parseFloat(item.outstanding);
            const totalAmount = parseFloat(item.amount);
            const paidAmount = parseFloat(item.paid_amount || 0);

            summary.total += amount;
            summary.totalPaid += paidAmount;
            summary.totalInvoiced += totalAmount;

            switch (item.aging_bucket) {
                case 'Current':
                    summary.current += amount;
                    break;
                case '1-30 Days':
                    summary['1-30'] += amount;
                    break;
                case '31-60 Days':
                    summary['31-60'] += amount;
                    break;
                case '61-90 Days':
                    summary['61-90'] += amount;
                    break;
                case 'Over 90 Days':
                    summary['90+'] += amount;
                    break;
            }
        });

        return summary;
    };

    const handleExport = async (format) => {
        try {
            const params = {
                client_id: filters.client_id,
                project_id: filters.project_id,
                end_date: filters.end_date,
                format
            };

            const response = await axios.get(`${backendUrl}/api/receivable`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    "Tenant-Origin": origin,
                    'Accept': '*/*'
                },
                params,
                responseType: 'blob'
            });

            const extension = format === 'csv' ? 'csv' : 'xls';
            const clientName = filters.client_id
                ? clients.find(c => c.value === filters.client_id)?.label
                : 'All_Clients';

            saveAs(new Blob([response.data]),
                `Receivable_Aging_${clientName}_${moment(filters.end_date).format('YYYYMMDD')}.${extension}`);
        } catch (error) {
            console.error("Error exporting report", error);
        }
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        // Apply the filters and reset to page 1
        setAppliedFilters({ ...filters });
        setPage(1);
        setIsSearched(true);
        // Fetch data with applied filters
        fetchAgingData(1);
    };

    const handleClear = () => {
        setFilters({
            client_id: '',
            project_id: '',
            end_date: moment().format('YYYY-MM-DD')
        });
        // Also clear applied filters and data
        setAppliedFilters({
            client_id: '',
            project_id: '',
            end_date: moment().format('YYYY-MM-DD')
        });
        setData([]);
        setPage(1);
        setIsSearched(false);
    };
    useEffect(() => {
        if (isSearched && appliedFilters.end_date) { // Check if filters have been applied at least once
            fetchAgingData(page);
        }
    }, [page, isSearched]);
    // Group data by client if no client filter is applied
    const groupedData = data.reduce((acc, item) => {
        if (!filters.client_id) {
            const key = item.client_id;
            if (!acc[key]) acc[key] = [];
            acc[key].push(item);
        } else {
            // If client filter is applied, just return all items in one group
            if (!acc['filtered']) acc['filtered'] = [];
            acc['filtered'].push(item);
        }
        return acc;
    }, {});

    // Calculate overall summary
    const overallSummary = calculateAgingSummary(data);

    return (
        <div className="main-content">
            <div className="page-content">
                <div className="container-fluid">
                    <div className="row">
                        <div className="col-12">
                            <div className="page-title-box d-flex align-items-center justify-content-between">
                                <div>
                                    <h4 className="mb-0 font-size-18">Accounts Receivable Aging Report</h4>
                                    <ol className="breadcrumb m-0 mt-2">
                                        <li className="breadcrumb-item">Reports</li>
                                        <li className="breadcrumb-item active">Receivable Aging</li>
                                    </ol>
                                </div>
                                <div className="d-flex gap-2">
                                    {/* <button
                                        onClick={() => handleExport('csv')}
                                        className="btn btn-outline-secondary btn-sm"
                                        disabled={loading || data.length === 0}
                                    >
                                        <i className="fas fa-file-csv me-1"></i> CSV
                                    </button> */}
                                    <button
                                        onClick={() => handleExport('excel')}
                                        className="btn btn-outline-secondary btn-sm"
                                        disabled={loading || data.length === 0}
                                    >
                                        <i className="fas fa-file-excel me-1"></i> Excel
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="row">
                        <div className="col-12">
                            <div className="card">
                                <div className="card-body">
                                    <form onSubmit={handleSubmit} className="mb-4">
                                        <div className="row g-3">
                                            <div className="col-md-3">
                                                <label className="form-label">Client</label>
                                                <Select
                                                    options={clients}
                                                    value={clients.find(option => option.value === filters.client_id)}
                                                    onChange={(selected) => setFilters({ ...filters, client_id: selected?.value || '' })}
                                                    isClearable
                                                    placeholder="All Clients"
                                                    className="react-select-container"
                                                    classNamePrefix="react-select"
                                                />
                                            </div>

                                            <div className="col-md-3">
                                                <label className="form-label">Project</label>
                                                <Select
                                                    options={projects}
                                                    value={projects.find(option => option.value === filters.project_id)}
                                                    onChange={(selected) => setFilters({ ...filters, project_id: selected?.value || '' })}
                                                    isClearable
                                                    placeholder="All Projects"
                                                    className="react-select-container"
                                                    classNamePrefix="react-select"
                                                />
                                            </div>

                                            <Col md={3}>
                                                <Form.Group>
                                                    <Form.Label>As of Date</Form.Label>

                                                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                                                        <DatePicker
                                                            views={["year", "month", "day"]}
                                                            format="DD-MM-YYYY"
                                                            value={
                                                                filters.end_date
                                                                    ? dayjs(filters.end_date, "DD-MM-YYYY")
                                                                    : null
                                                            }
                                                            onChange={(newValue) => {
                                                                setFilters({
                                                                    ...filters,
                                                                    end_date: newValue
                                                                        ? newValue.format("DD-MM-YYYY")
                                                                        : "",
                                                                });
                                                            }}
                                                            slotProps={{
                                                                textField: {
                                                                    size: "small",
                                                                    fullWidth: true,
                                                                    variant: "outlined",
                                                                },
                                                            }}
                                                        />
                                                    </LocalizationProvider>
                                                </Form.Group>
                                            </Col>


                                            <div className="col-md-3 d-flex align-items-end gap-2">
                                                <button
                                                    type="submit"
                                                    className="btn btn-primary flex-grow-1"
                                                    disabled={loading}
                                                >
                                                    {loading ? 'Generating...' : 'Generate Report'}
                                                </button>
                                                <button
                                                    type="button"
                                                    className="btn btn-outline-secondary"
                                                    onClick={handleClear}
                                                    disabled={loading}
                                                >
                                                    Clear
                                                </button>
                                            </div>
                                        </div>
                                    </form>

                                    {!isSearched ? (
                                        <div className="alert alert-light text-center border py-4">
                                            <i className="fas fa-filter fa-2x mb-3 text-muted"></i>
                                            <h5>Ready to Generate Report</h5>
                                            <p className="text-muted">Select your criteria above and click "Generate Report" to view aging data.</p>
                                        </div>
                                    ) : data.length === 0 && !loading ? (
                                        <div className="alert alert-info">
                                            No outstanding receivables found for the selected criteria.
                                        </div>
                                    ) : (
                                        <>
                                            {/* Summary Cards */}
                                            <div className="row mb-4">
                                                <div className="col-12">
                                                    <div className="d-flex justify-content-between align-items-center mb-3">
                                                        <h5 className="text-dark">
                                                            {filters.client_id
                                                                ? `Aging Report for ${clients.find(c => c.value === filters.client_id)?.label}`
                                                                : 'All Clients Aging Report'}
                                                        </h5>
                                                        <div className="text-muted">
                                                            <i className="far fa-calendar-alt me-1"></i>
                                                            As of {moment(filters.end_date).format('DD MMM YYYY')}
                                                        </div>
                                                    </div>
                                                </div>

                                                <div className="col-md-2 col-6">
                                                    <div className="card border-start border-start-3 border-start-success">
                                                        <div className="card-body">
                                                            <h6 className="text-muted mb-2">Total Invoiced</h6>
                                                            <h4 className="mb-0">{overallSummary.totalInvoiced.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</h4>
                                                        </div>
                                                    </div>
                                                </div>

                                                <div className="col-md-2 col-6">
                                                    <div className="card border-start border-start-3 border-start-info">
                                                        <div className="card-body">
                                                            <h6 className="text-muted mb-2">Total Paid</h6>
                                                            <h4 className="mb-0">{overallSummary.totalPaid.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</h4>
                                                        </div>
                                                    </div>
                                                </div>

                                                <div className="col-md-2 col-6">
                                                    <div className="card border-start border-start-3 border-start-primary">
                                                        <div className="card-body">
                                                            <h6 className="text-muted mb-2">Total Outstanding</h6>
                                                            <h4 className="mb-0 text-primary">{overallSummary.total.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</h4>
                                                        </div>
                                                    </div>
                                                </div>

                                                <div className="col-md-2 col-6">
                                                    <div className="card border-start border-start-3 border-start-warning">
                                                        <div className="card-body">
                                                            <h6 className="text-muted mb-2">Current</h6>
                                                            <h4 className="mb-0">{overallSummary.current.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</h4>
                                                        </div>
                                                    </div>
                                                </div>

                                                <div className="col-md-2 col-6">
                                                    <div className="card border-start border-start-3 border-start-orange">
                                                        <div className="card-body">
                                                            <h6 className="text-muted mb-2">1-60 Days</h6>
                                                            <h4 className="mb-0">{(overallSummary['1-30'] + overallSummary['31-60']).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</h4>
                                                        </div>
                                                    </div>
                                                </div>

                                                <div className="col-md-2 col-6">
                                                    <div className="card border-start border-start-3 border-start-danger">
                                                        <div className="card-body">
                                                            <h6 className="text-muted mb-2">Over 60 Days</h6>
                                                            <h4 className="mb-0">{(overallSummary['61-90'] + overallSummary['90+']).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</h4>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Detailed Table */}
                                            <div className="table-responsive">
                                                <table className="table table-hover align-middle">
                                                    <thead className="table-light">
                                                        <tr>
                                                            {!filters.client_id && <th>Client</th>}
                                                            <th>Invoice #</th>
                                                            <th>Date</th>
                                                            <th>Due Date</th>
                                                            <th className="text-end">Amount</th>
                                                            <th className="text-end">Received Amount</th>
                                                            <th className="text-end">Outstanding</th>
                                                            <th>Aging</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {Object.entries(groupedData).map(([clientId, items]) => {
                                                            const clientName = items[0]?.client_name || `Client ${clientId}`;
                                                            const summary = calculateAgingSummary(items);

                                                            return (
                                                                <React.Fragment key={clientId}>
                                                                    {!filters.client_id && (
                                                                        <tr className="table-group-divider">
                                                                            <td colSpan={9}>
                                                                                <div className="d-flex justify-content-between align-items-center">
                                                                                    <strong>{clientName}</strong>
                                                                                    <span className="badge bg-light text-dark">
                                                                                        Total: {summary.total.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                                                                    </span>
                                                                                </div>
                                                                            </td>
                                                                        </tr>
                                                                    )}
                                                                    {items.map((item, index) => (
                                                                        <tr key={index}>
                                                                            {!filters.client_id && <td>{item.client_name}</td>}
                                                                            <td>
                                                                                <span className="fw-medium">{item.invoice_id}</span>
                                                                            </td>
                                                                            <td>{moment(item.invoice_date).format("DD/MM/YYYY")}</td>
                                                                            <td>{moment(item.due_date).format("DD/MM/YYYY")}</td>
                                                                            <td className="text-end">{parseFloat(item.amount).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                                                                            <td className="text-end">{parseFloat(item.paid_amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                                                                            <td className="text-end fw-bold">{parseFloat(item.outstanding).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                                                                            <td>
                                                                                <span className={`badge ${item.aging_bucket === 'Current' ? 'bg-success' :
                                                                                    item.aging_bucket === '1-30 Days' ? 'bg-info' :
                                                                                        item.aging_bucket === '31-60 Days' ? 'bg-warning' :
                                                                                            item.aging_bucket === '61-90 Days' ? 'bg-orange' :
                                                                                                'bg-danger'
                                                                                    }`}>
                                                                                    {item.aging_bucket}
                                                                                </span>
                                                                            </td>
                                                                        </tr>
                                                                    ))}
                                                                </React.Fragment>
                                                            );
                                                        })}
                                                    </tbody>
                                                </table>

                                                <div className="d-flex justify-content-end pagination-sm">
                                                    <ReactPaginate
                                                        previousLabel={'<'}
                                                        nextLabel={'>'}
                                                        breakLabel={'...'}
                                                        pageCount={pagination.last_page}
                                                        marginPagesDisplayed={2}
                                                        pageRangeDisplayed={3}
                                                        onPageChange={handlePageClick}
                                                        containerClassName={'pagination pagination-rounded justify-content-end mb-2'}
                                                        pageClassName={'page-item'}
                                                        pageLinkClassName={'page-link'}
                                                        previousClassName={'page-item previous'}
                                                        previousLinkClassName={'page-link'}
                                                        nextClassName={'page-item next'}
                                                        nextLinkClassName={'page-link'}
                                                        breakClassName={'page-item'}
                                                        breakLinkClassName={'page-link'}
                                                        activeClassName={'active'}
                                                        forcePage={pagination.current_page - 1}
                                                    />
                                                </div>

                                            </div>
                                        </>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};
export default ReceivableAgingReport;
